import { Box, Button, Input, Select } from '@nerdwallet/currency';
import Accordion from '@nerdwallet/react-accordion';
import Trash from '@nerdwallet/react-icon/gizmo/Trash';
import FormatListBulleted from '@nerdwallet/react-icon/material/FormatListBulleted';
import get from 'lodash/get';
import { CSSProperties, Fragment, useEffect } from 'react';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { useMediaQuery } from '~/app/lib/use-media-query';
import {
  DynamicTextComponent,
  DynamicTextForm,
} from '../dynamic-text/dynamic-text-component';
import prosAndCons from './element-type-components/pros-and-cons';
import rewardsRates from './element-type-components/rewards-rates';
import {
  BULLET_LIST_STYLE_TYPE,
  BulletListItem,
  BulletListStyle,
  ELEMENT_TYPE,
  ElementType,
} from './types';
import { useDynamicBulletList } from './use-dynamic-bullet-list';
import { getMarketplaceOfferItems } from './utils';

import styles from './dynamic-bullet-list.module.scss';
import {
  personalLoans,
  PersonalLoansItems,
} from './element-type-components/personal-loans';

const MARKETPLACE_OFFER_ELEMENT_TYPES = [
  'prosAndCons',
  'rewardsRates',
  'marketingBullets',
  'personalLoans',
];

export type DynamicBulletListProps = {
  elementType?: ElementType;
  items?: BulletListItem[];
  imageUrl?: string;
  imageHeight?: number;
  imageWidth?: number;
  listStyleType?: BulletListStyle;
  listGap?: number;
  marketplaceOfferId?: string;
  rateAnnotation?: string;
  fontProperties?: {
    fontSize: number;
    tablet?: {
      fontSize: number;
    };
    mobile?: {
      fontSize: number;
    };
  };
};

export const DynamicBulletList = ({
  elementType = 'ul',
  items,
  imageUrl = '',
  imageHeight = 30,
  imageWidth = 30,
  listGap = 0,
  listStyleType = 'disc',
  marketplaceOfferId,
  rateAnnotation = '',
  fontProperties,
}: DynamicBulletListProps) => {
  const screenSize = useMediaQuery();
  const { marketplaceOffers } = useDynamicGrid();
  const isMobile = screenSize === 'sm';
  const marketplaceOfferItems = marketplaceOfferId
    ? getMarketplaceOfferItems(
        marketplaceOfferId,
        elementType,
        marketplaceOffers
      )
    : undefined;

  const itemsToDisplay = marketplaceOfferItems || items;

  if (
    !itemsToDisplay ||
    (Array.isArray(itemsToDisplay) &&
      (itemsToDisplay as BulletListItem[]).length === 0)
  ) {
    return <FormatListBulleted size={64} />;
  }

  const listItemsStyle =
    listGap && listGap !== 0
      ? ({
          display: 'flex',
          flexDirection: 'column',
          gap: `${listGap}px`,
        } as CSSProperties)
      : undefined;

  const imageW = `${imageWidth}px`;
  const imageH = `${imageHeight}px`;
  const listImageStyle =
    listStyleType === 'imageUrl'
      ? {
          paddingLeft: 40,
          background: `url(${imageUrl}) no-repeat left center`,
          backgroundSize: `${imageW} ${imageH}`,
          margin: `10px 0`,
        }
      : {};

  if (elementType === 'ol') {
    return (
      <ol
        className={styles.ordered}
        style={{ ...{ paddingLeft: '1em' }, ...listItemsStyle }}
      >
        {(itemsToDisplay as BulletListItem[])?.map((item: BulletListItem) => (
          <li style={listImageStyle} key={item.id}>
            <DynamicTextComponent {...item} />
          </li>
        ))}
      </ol>
    );
  }
  if (elementType === 'personalLoans') {
    return personalLoans({
      qualificationsBullets: (marketplaceOfferItems as PersonalLoansItems)
        ?.qualificationsBullets,
      availableTermLengths: (marketplaceOfferItems as PersonalLoansItems)
        ?.availableTermLengths,
      feesBullets: (marketplaceOfferItems as PersonalLoansItems)?.feesBullets,
      disclaimer: (marketplaceOfferItems as PersonalLoansItems)?.disclaimer,
      listStyleType: (listStyleType as BulletListStyle) || 'none',
    });
  }

  if (elementType === 'rewardsRates') {
    return rewardsRates({
      items: itemsToDisplay as BulletListItem[],
      listItemsStyle,
      rateAnnotation,
      fontProperties,
    });
  }

  if (elementType === 'prosAndCons') {
    return prosAndCons({
      items: itemsToDisplay as BulletListItem[],
      listImageStyle,
      listItemsStyle,
      isMobile,
      fontProperties,
    });
  }

  let bulletListClassName = '';
  if (listStyleType === 'dash') bulletListClassName = styles.mdash;
  if (listStyleType === 'imageUrl') bulletListClassName = styles.imageUrl;

  return (
    <ul
      className={bulletListClassName}
      style={{
        ...{ listStyle: listStyleType, paddingLeft: '1em' },
        ...listItemsStyle,
      }}
    >
      {(itemsToDisplay as BulletListItem[])?.map((item: BulletListItem) => {
        const sanitizedItem = {
          ...item,
          ...fontProperties,
        };
        return (
          <li style={listImageStyle} key={item.id}>
            <DynamicTextComponent {...sanitizedItem} />
          </li>
        );
      })}
    </ul>
  );
};

export const DynamicBulletListForm = ({
  fieldPath = '',
}: {
  fieldPath?: string;
}) => {
  const {
    /* Base props */
    currentComponent,
    componentPath,

    /* List props */
    elementType,
    listStyleType,
    handleElementTypeChange,
    handleListStyleTypeChange,
    setElementType,
    setListStyleType,
    imageUrl,
    imageHeight,
    imageWidth,
    marketplaceOfferId,
    marketplaceOfferOptions,
    listGap,
    rateAnnotation,
    handleImageUrlChange,
    handleImageHeightChange,
    handleImageWidthChange,
    handleMarketplaceOfferChange,
    handleListGapChange,
    handleRateAnnotationChange,
    setImageUrl,
    setImageHeight,
    setImageWidth,
    setMarketplaceOfferId,
    setListGap,
    setRateAnnotation,
    fontProperties,
    handleFontSizeChange,
    handleFontSizeTabletChange,
    handleFontSizeMobileChange,
    setFontProperties,

    /* List data props */
    items,
    setItems,
    handleAddItem,
    handleDeleteItem,
  } = useDynamicBulletList({
    fieldPath,
  });

  useEffect(() => {
    const fieldPathValue = fieldPath
      ? `props.${componentPath}.${fieldPath}` // If fieldPath is provided, update the nested field
      : `props.${componentPath}`; // If fieldPath is not provided, update the top-level field
    setElementType(get(currentComponent, `${fieldPathValue}.elementType`, ''));
    setImageHeight(get(currentComponent, `${fieldPathValue}.imageHeight`, ''));
    setImageUrl(get(currentComponent, `${fieldPathValue}.imageUrl`, ''));
    setImageWidth(get(currentComponent, `${fieldPathValue}.imageWidth`, ''));
    setItems(get(currentComponent, `${fieldPathValue}.items`, []));
    setListStyleType(
      get(currentComponent, `${fieldPathValue}.listStyleType`, '')
    );
    setMarketplaceOfferId(
      get(currentComponent, `${fieldPathValue}.marketplaceOfferId`, '')
    );
    setListGap(get(currentComponent, `${fieldPathValue}.listGap`, ''));
    setRateAnnotation(
      get(currentComponent, `${fieldPathValue}.rateAnnotation`, '')
    );
    setFontProperties(
      get(currentComponent, `${fieldPathValue}.fontProperties`, {})
    );
  }, [
    fieldPath,
    currentComponent,
    componentPath,
    setElementType,
    setImageHeight,
    setImageUrl,
    setImageWidth,
    setItems,
    setListGap,
    setListStyleType,
    setMarketplaceOfferId,
    setRateAnnotation,
    setFontProperties,
  ]);

  return (
    <Box paddingY={2}>
      <Box paddingY={2}>
        <Select
          fullWidth
          value={elementType}
          onChange={handleElementTypeChange}
          label="List Type"
          options={ELEMENT_TYPE}
        />
      </Box>

      {!['prosAndCons', 'rewardsRates'].includes(elementType) && (
        <Box paddingY={2}>
          <Select
            fullWidth
            value={listStyleType}
            onChange={handleListStyleTypeChange}
            label="List Style"
            options={BULLET_LIST_STYLE_TYPE}
          />
        </Box>
      )}

      {listStyleType === 'imageUrl' && (
        <Fragment>
          <Box paddingY={2}>
            <Input
              type="text"
              value={imageUrl}
              placeholder="Image URL"
              onChange={handleImageUrlChange}
              fullWidth
            />
          </Box>
          <Box
            paddingY={2}
            display="flex"
            flexDirection="row"
            style={{ gap: '1em' }}
          >
            <Input
              value={imageHeight}
              placeholder="Image Height"
              onChange={handleImageHeightChange}
              fullWidth
            />
            <Input
              value={imageWidth}
              placeholder="Image Width"
              onChange={handleImageWidthChange}
              fullWidth
            />
          </Box>
        </Fragment>
      )}

      {MARKETPLACE_OFFER_ELEMENT_TYPES.includes(elementType) && (
        <Box paddingY={2}>
          <Select
            fullWidth
            value={marketplaceOfferId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              handleMarketplaceOfferChange(e.target.value)
            }
            label="Marketplace Offer"
            options={marketplaceOfferOptions()}
          />
        </Box>
      )}

      {elementType === 'rewardsRates' && (
        <Box paddingY={2}>
          <Input
            fullWidth
            value={rateAnnotation}
            onChange={handleRateAnnotationChange}
            label="Rate Annotation"
          />
        </Box>
      )}

      <Box paddingY={2}>
        <Input
          value={listGap}
          label="List Items Gap (px)"
          onChange={handleListGapChange}
          fullWidth
        />
      </Box>

      {['prosAndCons', 'rewardsRates', 'marketingBullets'].includes(
        elementType
      ) && (
        <Box
          paddingTop={2}
          id="font-options-header"
          className={styles.accordionWrapper}
        >
          <Accordion
            items={[
              {
                title: (
                  <Box className={styles.accordionHeaderContainer}>
                    <span>Font Options</span>
                  </Box>
                ),
                children: (
                  <Fragment>
                    <Box padding={2}>
                      <Input
                        label="Font Size"
                        fullWidth
                        value={fontProperties?.fontSize}
                        onChange={handleFontSizeChange}
                      />
                    </Box>

                    <Box
                      paddingTop={2}
                      id="font-options-tablet"
                      className={styles.accordionWrapper}
                    >
                      <Accordion
                        items={[
                          {
                            title: (
                              <Box className={styles.accordionHeaderContainer}>
                                <span>Tablet</span>
                              </Box>
                            ),
                            children: (
                              <Box padding={2}>
                                <Input
                                  label="Font Size"
                                  fullWidth
                                  value={fontProperties?.tablet?.fontSize}
                                  onChange={handleFontSizeTabletChange}
                                />
                              </Box>
                            ),
                            reducedPadding: true,
                          },
                        ]}
                      />
                    </Box>
                    <Box
                      paddingTop={2}
                      id="font-options-mobile"
                      className={styles.accordionWrapper}
                    >
                      <Accordion
                        items={[
                          {
                            title: (
                              <Box className={styles.accordionHeaderContainer}>
                                <span>Mobile</span>
                              </Box>
                            ),
                            children: (
                              <Box padding={2}>
                                <Input
                                  label="Font Size"
                                  fullWidth
                                  value={fontProperties?.mobile?.fontSize}
                                  onChange={handleFontSizeMobileChange}
                                />
                              </Box>
                            ),
                            reducedPadding: true,
                          },
                        ]}
                      />
                    </Box>
                  </Fragment>
                ),
                reducedPadding: true,
              },
            ]}
          />
        </Box>
      )}

      {(elementType !== 'prosAndCons' || !marketplaceOfferId) &&
        elementType !== 'rewardsRates' &&
        elementType !== 'marketingBullets' && (
          <Box paddingY={2}>
            <p>List Items</p>
            <Box paddingY={2}>
              {items.map((item, index) => (
                <div className={styles.accordionWrapper} key={item.id}>
                  <Accordion
                    key={item.id}
                    items={[
                      {
                        title: (
                          <Box className={styles.itemHeaderContainer}>
                            <span>{`Item ${index + 1}`}</span>
                            <Trash
                              onClick={() => handleDeleteItem(index)}
                              size={15}
                              title="delete this element"
                              className="trash"
                            />
                          </Box>
                        ),
                        children: (
                          <Box key={item.id}>
                            <DynamicTextForm
                              fieldPath={
                                fieldPath
                                  ? `${fieldPath}.items.${index}`
                                  : `items.${index}`
                              }
                            />
                          </Box>
                        ),
                        reducedPadding: true,
                      },
                    ]}
                  />
                </div>
              ))}
            </Box>

            <Button variant="secondary" block onClick={handleAddItem} fullWidth>
              Add item
            </Button>
          </Box>
        )}
    </Box>
  );
};
